import ModalSectionHeader from "@/components/ui/ModalSectionHeader";
import type { TableProps } from "antd";
import { Radio, Table } from "antd";
import React from "react";
import type { EvaluationResultDataType } from "../../type";
import { getScoreColor } from "../../utils";
import styles from "./index.module.less";
import { useTranslation } from "react-i18next";

type Props = {
  evaluationData?: EvaluationResultDataType[];
};

export const PerformanceEvaluationResult: React.FC<Props> = ({
  evaluationData,
}) => {
  const { t } = useTranslation("supplierPerformance");
  // 计算每个维度的指标数量，用于行合并
  const getDimensionRowSpan = (
    record: EvaluationResultDataType,
    index: number,
  ) => {
    if (!evaluationData) return 1;

    const dimensionRows = evaluationData.filter(
      (item) => item.rater === record.rater,
    );
    const firstIndex = evaluationData.findIndex(
      (item) => item.rater === record.rater,
    );

    if (index === firstIndex) {
      return dimensionRows.length;
    }
    return 0;
  };

  const columns: TableProps<EvaluationResultDataType>["columns"] = [
    {
      title: t("考核维度"),
      dataIndex: "dimensionName",
      key: "dimensionName",
      width: 120,
      align: "center",
      onCell: (record, rowIndex) => {
        const rowSpan = getDimensionRowSpan(record, rowIndex!);
        return {
          rowSpan: rowSpan,
        };
      },
      render: (text) => {
        return (
          <div style={{ fontWeight: "bold", fontSize: "18px" }}>{text}</div>
        );
      },
    },

    {
      title: t("考核项"),
      dataIndex: "indicator2Name",
      key: "indicator2Name",
      width: 120,
      align: "center",
      render: (text) => <div style={{ fontWeight: "500" }}>{text}</div>,
    },
    {
      title: <span style={{ color: "#ef4444" }}>{t("差 (25分)")}</span>,
      dataIndex: "level1Remarks",
      key: "level1Remarks",
      align: "center",
      render: (text, record) => (
        <div style={{ padding: "8px 0", cursor: "pointer" }}>
          <Radio
            checked={record.score === 25}
            style={{ marginBottom: "8px" }}
          />
          <div style={{ fontSize: "14px", color: "#6b7280", padding: "0 8px" }}>
            {text}
          </div>
        </div>
      ),
    },
    {
      title: <span style={{ color: "#fb923c" }}>{t("一般 (50分)")}</span>,
      dataIndex: "level2Remarks",
      key: "level2Remarks",
      align: "center",
      render: (text, record) => (
        <div style={{ padding: "8px 0", cursor: "pointer" }}>
          <Radio
            checked={record.score === 50}
            style={{ marginBottom: "8px" }}
          />
          <div style={{ fontSize: "14px", color: "#6b7280", padding: "0 8px" }}>
            {text}
          </div>
        </div>
      ),
    },
    {
      title: <span style={{ color: "#3b82f6" }}>{t("良好 (75分)")}</span>,
      dataIndex: "level3Remarks",
      key: "level3Remarks",
      align: "center",
      render: (text, record) => (
        <div style={{ padding: "8px 0", cursor: "pointer" }}>
          <Radio
            checked={record.score === 75}
            style={{ marginBottom: "8px" }}
          />
          <div style={{ fontSize: "14px", color: "#6b7280", padding: "0 8px" }}>
            {text}
          </div>
        </div>
      ),
    },
    {
      title: <span style={{ color: "#16a34a" }}>{t("优秀 (100分)")}</span>,
      dataIndex: "level4Remarks",
      key: "level4Remarks",
      align: "center",
      render: (text, record) => (
        <div style={{ padding: "8px 0", cursor: "pointer" }}>
          <Radio
            checked={record.score === 100}
            style={{ marginBottom: "8px" }}
          />
          <div style={{ fontSize: "14px", color: "#6b7280", padding: "0 8px" }}>
            {text}
          </div>
        </div>
      ),
    },
    {
      title: t("评估人"),
      dataIndex: "raterUserName",
      key: "raterUserName",
      width: 120,
      align: "center",
      onCell: (record, rowIndex) => {
        const rowSpan = getDimensionRowSpan(record, rowIndex!);
        return {
          rowSpan,
        };
      },
      render: (text) => {
        return (
          <div style={{ fontWeight: "bold", fontSize: "18px" }}>{text}</div>
        );
      },
    },
    {
      title: t("得分"),
      dataIndex: "dimensionScore",
      key: "dimensionScore",
      width: 120,
      align: "center",
      onCell: (record, rowIndex) => {
        const rowSpan = getDimensionRowSpan(record, rowIndex!);
        return {
          rowSpan: rowSpan,
        };
      },
      render: (text) => {
        const color = getScoreColor(text);
        return (
          <div style={{ fontWeight: "bold", fontSize: "18px", color }}>
            {text}
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.card}>
      <ModalSectionHeader title={t("供应商绩效评分")} />
      <Table
        columns={columns}
        dataSource={evaluationData}
        pagination={false}
        bordered
        size="middle"
      />
    </div>
  );
};
